import { useEffect, useState } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { approximateGasFees } from '@src/hooks/approximateGasFees'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { IExchangeData } from '@src/types/exchange'
import { PrecomputeGasResults } from '@src/types/gasPrices'
import { Balance } from '@src/utils/balance'
import { createContextLogger } from '@src/utils/logger'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useGasEstimation')

interface UseGasEstimationParams {
    sellData: IExchangeData
    sellAllowance: bigint | undefined
    precomputedGasPrices: PrecomputeGasResults | undefined
    nativeTokenPricesInUSD: Record<number, number>
    isCCTP: boolean
    isWrapOrUnwrapOnly: boolean
}

export function useGasEstimation({
    sellData,
    sellAllowance,
    precomputedGasPrices,
    nativeTokenPricesInUSD,
    isCCTP,
    isWrapOrUnwrapOnly,
}: UseGasEstimationParams) {
    const [gasFeeString, setGasFeeString] = useState<string>('Loading...')
    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)

    const { nativeBalance, isNativeBalanceLoading } = useExchangeBalances()
    const { setHasInsufficientGas, setGasAmountNeededString } = useExchangeState()

    const logTotalGas = (sellData: IExchangeData, totalGas: bigint) => {
        logger.info(`Gas needed = ${formatTotalGasNeeded(sellData, totalGas)}`)
    }

    const formatTotalGasNeeded = (sellData: IExchangeData, totalGas: bigint) => {
        try {
            return `${new Balance(getGasTokenProps(sellData.chainId)).setValue(totalGas).formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${getGasTokenProps(sellData.chainId).symbol}`
        } catch {
            return ''
        }
    }

    useEffect(
        () => {
            const updateGasFees = async () => {
                // Return early if we don't have precomputed prices
                if (!precomputedGasPrices) {
                    setGasFeeString('Loading...')
                    return
                }

                try {
                    if (sellData.chainId === ChainId.Solana) {
                        const fees = approximateGasFees(
                            precomputedGasPrices,
                            sellData,
                            true, // Solana has no approvals and cctp is true
                            sellData.token.native,
                            false,
                            isWrapOrUnwrapOnly
                        )

                        logger.info(`Approximated gas fees for SOL -> EVM CCTP:`, fees)

                        if (!fees.solanaResults) {
                            setGasFeeString('Error')
                            return
                        }

                        const totalGas = fees.solanaResults.feeInLamports
                        const feeInUSD =
                            nativeTokenPricesInUSD[sellData.chainId] *
                            new Balance(getGasTokenProps(sellData.chainId)).setValue(totalGas).toNumber()
                        setGasFeeString(`$${Math.round(1000000 * feeInUSD) / 1000000}`)

                        logTotalGas(sellData, totalGas)
                        setGasAmountNeededString(formatTotalGasNeeded(sellData, totalGas))
                        if (nativeBalance && totalGas > nativeBalance.bigintValue) {
                            logger.info('Native balance is insufficient to execute swaps.')
                            setHasInsufficientGas(true)
                        } else {
                            if (nativeBalance === undefined) {
                                logger.error('Native balance is undefined.')
                                setHasInsufficientGas(true)
                            } else {
                                logger.success('Native balance is ok.')
                                setHasInsufficientGas(false)
                            }
                        }
                    } else if (sellData.chainId === ChainId.Tron) {
                        const isApprovalNeeded =
                            sellData.token.native === false &&
                            typeof sellAllowance !== 'undefined' &&
                            sellAllowance < sellData.value

                        const fees = approximateGasFees(
                            precomputedGasPrices,
                            sellData,
                            true,
                            sellData.token.native,
                            isApprovalNeeded,
                            isWrapOrUnwrapOnly
                        )
                        logger.info(`Approximated gas fees for TRON -> Any:`, fees)

                        if (!fees.tronResults || !fees.feeMetrics.TronParams) {
                            setGasFeeString('Error')
                            return
                        }

                        let totalGas = fees.tronResults.totalEnergyUsed * fees.feeMetrics.TronParams.energyPrice
                        totalGas += fees.tronResults.totalBandWidthUsed * fees.feeMetrics.TronParams.bandwidthPrice

                        const feeInUSD =
                            nativeTokenPricesInUSD[ChainId.Tron] *
                            new Balance(getGasTokenProps(ChainId.Tron)).setValue(BigInt(totalGas)).toNumber()

                        setGasFeeString(`$${Math.round(1000000 * feeInUSD) / 1000000}`)

                        logTotalGas(sellData, totalGas)
                        setGasAmountNeededString(formatTotalGasNeeded(sellData, totalGas))
                        if (nativeBalance && totalGas > nativeBalance.bigintValue) {
                            logger.info('Native balance is insufficient to execute swaps.')
                            setHasInsufficientGas(true)
                        } else {
                            if (nativeBalance === undefined) {
                                logger.error('Native balance is undefined.')
                                setHasInsufficientGas(true)
                            } else {
                                logger.success('Native balance is ok.')
                                setHasInsufficientGas(false)
                            }
                        }
                    } else {
                        // EVM chains
                        const isApprovalNeeded =
                            sellData.token.native === false &&
                            typeof sellAllowance !== 'undefined' &&
                            sellAllowance < sellData.value

                        const fees = approximateGasFees(
                            precomputedGasPrices,
                            sellData,
                            isCCTP,
                            sellData.token.native,
                            isApprovalNeeded,
                            isWrapOrUnwrapOnly
                        )
                        logger.info(`Approximated gas fees for EVM -> Any:`, fees)

                        const totalGas = fees.totalGasUnits * fees.totalFeePerGasUnit
                        const feeInUSD =
                            nativeTokenPricesInUSD[sellData.chainId] *
                            new Balance(getGasTokenProps(sellData.chainId)).setValue(BigInt(totalGas)).toNumber()

                        setGasFeeString(`$${Math.round(1000000 * feeInUSD) / 1000000}`)

                        logTotalGas(sellData, totalGas)
                        setGasAmountNeededString(formatTotalGasNeeded(sellData, totalGas))
                        if (nativeBalance && totalGas > nativeBalance.bigintValue) {
                            logger.info('Native balance is insufficient to execute swaps.')
                            setHasInsufficientGas(true)
                        } else {
                            if (nativeBalance === undefined) {
                                logger.error('Native balance is undefined.')
                                setHasInsufficientGas(true)
                            } else {
                                logger.success('Native balance is ok.')
                                setHasInsufficientGas(false)
                            }
                        }
                    }
                } catch (error) {
                    logger.error('Error computing gas fees', error)
                    setGasFeeString('Error')
                }
            }

            updateGasFees()
        },

        // DO NOT include sellData.value. Gas costs are independent of the sell value
        [
            precomputedGasPrices,
            nativeBalance,
            isNativeBalanceLoading,
            sellData.chainId,
            sellData.token,
            sellAllowance,
            nativeTokenPricesInUSD,
            isCCTP,
            isWrapOrUnwrapOnly,
        ]
    )

    return gasFeeString
}
